/**
 * @fileoverview Bohomoon – Charm Bundle line item controller for cart & mini-cart.
 * @module boho-charm-line-item
 *
 * @summary Contents
 *  - {@link CharmLineItem} — custom element controlling the bundle line item UI.
 *  - {@link typedef:BundleToggleLabels|BundleToggleLabels} — label map used for the dropdown toggle.
 *  - {@link event:line-item-quantity:change:end} — emitted after a line item quantity update completes.
 *  - {@link event:cart:updated} — emitted globally with the updated cart payload.
 *  - {@link event:cart:refresh} — emitted globally to trigger section re-render of mini-cart.
 */

/**
 * @typedef {Object} BundleToggleLabels
 * @property {string} expand - Label when the dropdown can be expanded.
 * @property {string} collapse - Label when the dropdown can be collapsed.
 */

/**
 * Fired by a line item when its quantity update finishes.
 * @event line-item-quantity:change:end
 * @type {CustomEvent<{ cart: object, newLineQuantity: number }>}
 */

/**
 * Global cart update broadcast with the full cart payload.
 * @event cart:updated
 * @type {CustomEvent<{ cart: object }>}
 */

/**
 * Global cart refresh request (used by mini-cart sections rendering).
 * @event cart:refresh
 * @type {CustomEvent<{ cart: object, replacementDelay: number }>}
 */

/**
 * Controls the merged bundle line item UI: expands/collapses the list of items,
 * updates labels, and handles remove links by calling the Cart Change API.
 * @class CharmLineItem
 * @extends HTMLElement
 * @listens cart-drawer:closed
 * @fires line-item-quantity:change:end
 * @fires cart:updated
 * @fires cart:refresh
 */
class CharmLineItem extends HTMLElement {
  constructor() {
    super();
    /** @private @type {HTMLDetailsElement|null} */
    this.dropdownItemsWrapper = this.querySelector(
      '[data-charm-dropdown-wrapper]',
    );

    /** @private @type {HTMLElement|null} */
    this.dropdownItems = this.querySelector('[data-charm-dropdown-items]');
    /** @private @type {HTMLElement|null} */
    this.itemsToggle = this.querySelector('[data-charm-dropdown-toggle]');
    /** @private @type {HTMLAnchorElement|null} */
    this.removeLink = this.querySelector('[data-charm-parent-remove]');
  }

  /**
   * Lifecycle hook: binds event listeners and loads toggle labels from theme variables.
   * @returns {void}
   */
  connectedCallback() {
    /** @type {BundleToggleLabels} */
    this.toggleLabels = {
      expand: window.themeVariables.customiser.bundleItem.expand,
      collapse: window.themeVariables.customiser.bundleItem.collapse,
    };

    this.itemsToggle.addEventListener('click', () => this.toggleDropdown());
    this.removeLink.addEventListener('click', (evt) =>
      this._updateFromLink(evt, this.removeLink.href),
    );

    document.addEventListener('cart-drawer:closed', () => {
      this.dropdownItemsWrapper.open = false;
      this.itemsToggle.classList.toggle(
        'charm-parent-product__item-toggle--open',
        false,
      );
    });
  }

  /**
   * Toggles the dropdown (details) element and updates the toggle label/chevron state.
   * @returns {void}
   */
  toggleDropdown() {
    this.dropdownItemsWrapper.open = !this.dropdownItemsWrapper.open;

    const isOpen = this.dropdownItemsWrapper.open;

    this.itemsToggle.classList.toggle(
      'charm-parent-product__item-toggle--open',
      isOpen,
    );

    this.updateToggleLabel();
  }

  /**
   * Updates the text label inside the toggle based on the open/closed state.
   * No-op if the label element cannot be found.
   * @returns {void}
   */
  updateToggleLabel() {
    const labelEl = this.itemsToggle.querySelector('[data-toggle-label]');
    const labelText = this.dropdownItemsWrapper.open
      ? this.toggleLabels.collapse
      : this.toggleLabels.expand;

    if (labelEl) labelEl.textContent = labelText;
  }

  /**
   * Intercepts a remove link to perform a cart change via fetch, then animates and removes the item.
   * Falls back to hard navigation on the cart page.
   * @param {MouseEvent} evt
   * @param {string} link - The URL from the remove anchor (may include line/id/quantity params).
   * @fires line-item-quantity:change:end
   * @fires cart:updated
   * @fires cart:refresh
   * @returns {Promise<void>}
   */
  async _updateFromLink(evt, link) {
    evt.preventDefault();
    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.href = link;
      return;
    }
    const changeUrl = new URL(
        link,
        `https://${window.themeVariables.routes.host}`,
      ),
      searchParams = changeUrl.searchParams,
      line = searchParams.get('line'),
      id = searchParams.get('id'),
      quantity = parseInt(searchParams.get('quantity'));

    this.classList.add('is-removing');

    const response = await fetch(
      `${window.themeVariables.routes.cartChangeUrl}.js`,
      {
        body: JSON.stringify({ line, id, quantity, sections: ['mini-cart'] }),
        credentials: 'same-origin',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    const cartContent = await response.json();
    // Notify listeners that the quantity update finished for this line item
    this.dispatchEvent(
      new CustomEvent('line-item-quantity:change:end', {
        bubbles: true,
        detail: { cart: cartContent, newLineQuantity: quantity },
      }),
    );

    await this.animate(
      { opacity: [1, 0], height: [`${this.clientHeight}px`, 0] },
      { duration: 300, easing: 'ease' },
    ).finished;
    this.remove();

    // Broadcast the updated cart payload globally
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          cart: cartContent,
        },
      }),
    );
    // Request UI refresh (mini-cart sections) with an appropriate replacement delay
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay: quantity === 0 ? 600 : 750,
        },
      }),
    );
  }
}

if (!customElements.get('charm-line-item')) {
  /** @see CharmLineItem */
  customElements.define('charm-line-item', CharmLineItem);
}
